import {
  BaseHTMLAttributes,
  GraphCtxProvider,
  GraphScale,
  renderMaybeFn,
  TimeRange,
} from '@mi-front/core';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import React, { MutableRefObject, RefObject, useRef } from 'react';
import { getFadeSidesStyle } from '../../../helpers/stream';
import { useMeasureDirty } from '../../../hooks/useMeasureDirty';
import { AxisX } from '../axis';

const DEFAULT_AXIS_HEIGHT = 25;

const StyledWrapper = styled('div')({
  position: 'relative',
});

const StyledSvg = styled('svg')({
  position: 'absolute',
  pointerEvents: 'none',
});

const StyledHtml = styled('div')({
  position: 'relative',
});

interface Props extends BaseHTMLAttributes {
  readonly range: TimeRange<number>;
  readonly height: number;
  readonly width?: number;
  readonly renderSvg?: (scale: GraphScale) => React.ReactNode;
  readonly renderHtml?: (scale: GraphScale) => React.ReactNode;
  readonly disableHtmlEvents?: boolean;
  readonly showAxis?: boolean;
  readonly tz?: string;
  readonly compressed?: boolean;
  readonly containerRef?: MutableRefObject<HTMLDivElement | undefined>;
  readonly htmlBehindSvg?: boolean;
  readonly fadeSides?: boolean | 'left' | 'right';
  readonly hideSeconds?: boolean;
}

export type StreamProps = Props;

Stream.defaultProps = {
  svgChildren: null,
  showAxis: true,
  disableHtmlEvents: false,
};

export function Stream(p: Props) {
  const {
    range,
    height,
    renderSvg,
    renderHtml,
    disableHtmlEvents,
    showAxis,
    className,
    tz,
    compressed,
    containerRef,
    htmlBehindSvg = false,
    fadeSides = false,
    hideSeconds,
  } = p;

  const axisHeight = showAxis ? DEFAULT_AXIS_HEIGHT : 0;
  const ref = useRef(null);
  const measures = useMeasureDirty(ref);
  const width = p.width || measures.width;

  return (
    <Box style={{ width: '100%', display: 'block' }} ref={ref}>
      {!!width && (
        <GraphCtxProvider
          value={{
            range,
            width,
            height: height - axisHeight,
            tz,
            compressed,
          }}
        >
          {(scale) => {
            return (
              <StyledWrapper
                className={classNames('stream__wrapper', className)}
                style={{
                  height,
                  width,
                }}
                ref={containerRef as RefObject<HTMLDivElement>}
              >
                <StyledSvg
                  className={classNames('stream__svg')}
                  style={{
                    height,
                    width,
                    zIndex: htmlBehindSvg ? 2 : undefined,
                    ...getFadeSidesStyle(fadeSides),
                  }}
                >
                  {showAxis && (
                    <AxisX y={height - axisHeight} axisId="thumbaxis" hideSeconds={hideSeconds} />
                  )}
                  {renderSvg && renderMaybeFn(renderSvg, scale)}
                </StyledSvg>

                {renderHtml && (
                  <StyledHtml
                    className={classNames('stream__html')}
                    style={{
                      width,
                      height: height - axisHeight,
                      pointerEvents: disableHtmlEvents ? 'none' : undefined,
                      position: htmlBehindSvg ? 'absolute' : undefined,
                      // When htmlBehindSvg is false, use zIndex: 0 so this div creates a stacking context.
                      // Otherwise the selection overlay (zIndex: -1000 inside TimeSelection) is stacked
                      // in the wrapper's context and ends up behind the SVG waveform, hiding the overlay.
                      zIndex: htmlBehindSvg ? 1 : 0,
                    }}
                  >
                    {renderHtml(scale)}
                  </StyledHtml>
                )}
              </StyledWrapper>
            );
          }}
        </GraphCtxProvider>
      )}
    </Box>
  );
}
